import { useEffect, useState } from "react"
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material"
import { taskApi, userApi } from "../../api"
import { usePermissions } from "../../permissions"
import ClientSelect from "../../components/ClientSelect"
import ProjectSelect from "../../components/ProjectSelect"

/** Organization 0 is the back office — a tenant like any other, not a blank. */
const BACK_OFFICE_ORG_ID = "0"

const EMPTY = {
  title: "",
  description: "",
  type: "task",
  priority: "medium",
  status_id: 2,
  client_id: "",
  project_id: "",
  due_date: "",
}

/**
 * @param {object} [project] When given, the task is being raised FROM a project
 *   — so the project and its client are settled and the two selects are replaced
 *   by a line saying which. Offering a project picker on a project's own page is
 *   offering a choice that has already been made, and a chance to make it wrong.
 */
export default function TaskDialog({ open, statuses, projects, clients, project, onClose, onCreated }) {
  const { isBackOffice, organizationId } = usePermissions()
  const [form, setForm] = useState(EMPTY)
  const [assignee, setAssignee] = useState(null)
  const [directory, setDirectory] = useState([])
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    // A real organization id from the start. Back office is "0", not "nothing".
    setForm({
      ...EMPTY,
      client_id: project ? String(project.client_id) : isBackOffice ? BACK_OFFICE_ORG_ID : organizationId || "",
      project_id: project ? project.id : "",
    })
    setAssignee(null)
    setError(null)
    userApi
      .directory()
      .then((data) => setDirectory(data.users || []))
      .catch(() => setDirectory([]))
  }, [open, isBackOffice, organizationId, project])

  const set = (key) => (event) =>
    setForm((f) => ({
      ...f,
      [key]: event.target.value,
      // A project from a different client would be rejected by the server.
      ...(key === "client_id" ? { project_id: "" } : {}),
    }))

  const submit = async (event) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await taskApi.create({
        title: form.title.trim(),
        description: form.description.trim() || null,
        type: form.type,
        priority: form.priority,
        status_id: Number(form.status_id),
        client_id: form.client_id === "" ? undefined : form.client_id,
        project_id: form.project_id === "" ? null : Number(form.project_id),
        assignee_id: assignee?.id ?? null,
        due_date: form.due_date || null,
      })
      onCreated(result.task)
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setBusy(false)
    }
  }

  const visibleProjects = form.client_id ? projects.filter((p) => String(p.client_id) === String(form.client_id)) : projects
  const eligibleAssignees = form.client_id
    ? directory.filter((u) => String(u.organization_id) === String(form.client_id) || String(u.organization_id) === "0")
    : directory

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>{project ? `New task in ${project.code}` : "New task"}</DialogTitle>
        <DialogContent dividers>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField label="Title" value={form.title} onChange={set("title")} fullWidth required autoFocus />
            </Grid>
            <Grid size={12}>
              <TextField label="Description" value={form.description} onChange={set("description")} fullWidth multiline rows={3} />
            </Grid>

            {/* Organization 0 is a real tenant and is listed like any other.
                It used to be a synthetic option with an empty value, which read
                as blank once chosen — indistinguishable from choosing nothing —
                and was then dropped from the request by `|| undefined`. */}
            {project ? (
              <Grid size={12}>
                <Typography variant="caption" color="text.secondary">
                  {[
                    String(project.client_id) === BACK_OFFICE_ORG_ID ? null : project.client_label,
                    project.code,
                    project.name,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              </Grid>
            ) : null}

            {!project && isBackOffice && clients.length > 0 ? (
              <Grid size={{ xs: 12, sm: 6 }}>
                <ClientSelect
                  clients={clients}
                  value={form.client_id}
                  onChange={(id) =>
                    // A project from a different client would be rejected by the server.
                    setForm((f) => ({ ...f, client_id: id, project_id: "" }))
                  }
                  fullWidth
                />
              </Grid>
            ) : null}

            {!project ? (
              <Grid size={{ xs: 12, sm: 6 }}>
                <ProjectSelect
                  projects={visibleProjects}
                  value={form.project_id}
                  onChange={(id) => setForm((f) => ({ ...f, project_id: id }))}
                  emptyLabel="No project"
                  fullWidth
                />
              </Grid>
            ) : null}

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField select label="Type" value={form.type} onChange={set("type")} fullWidth>
                <MenuItem value="task">Task</MenuItem>
                <MenuItem value="issue">Issue</MenuItem>
                <MenuItem value="bug">Bug</MenuItem>
                <MenuItem value="request">Request</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField select label="Priority" value={form.priority} onChange={set("priority")} fullWidth>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField select label="Status" value={form.status_id} onChange={set("status_id")} fullWidth>
                {statuses.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    {status.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 7 }}>
              <Autocomplete
                options={eligibleAssignees}
                value={assignee}
                onChange={(_, value) => setAssignee(value)}
                getOptionLabel={(option) => option.name || option.email}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Assignee" placeholder="Unassigned" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField
                label="Due date"
                type="date"
                value={form.due_date}
                onChange={set("due_date")}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={busy} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={busy || !form.title.trim()}>
            {busy ? "Creating…" : "Create task"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
